/**
 * Apply resolved page metadata to the document head.
 */

import { PUBLIC_ORIGIN } from './catalog';
import type { PageMeta } from './types';

function ensureNamedMeta(name: string): HTMLMetaElement {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  return el;
}

function ensurePropertyMeta(property: string): HTMLMetaElement {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  return el;
}

function setCanonical(href: string | null): void {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Sync title, description, robots, and (for public pages) light social tags.
 * Private routes get noindex and never receive a canonical URL.
 */
export function applyPageMeta(meta: PageMeta, documentTitle: string): void {
  const description = meta.description.trim() || 'BuzzBit X';
  const titleForSocial = documentTitle.trim() || 'BuzzBit X';

  document.title = titleForSocial;

  ensureNamedMeta('description').content = description;
  ensureNamedMeta('robots').content = meta.indexable ? 'index, follow' : 'noindex, nofollow';

  // Keep og/twitter titles in sync for public pages; private pages stay generic
  // and noindex so crawlers that ignore robots still do not get a private URL
  // framed as a share card with a misleading homepage og:url.
  ensurePropertyMeta('og:title').content = titleForSocial;
  ensurePropertyMeta('og:description').content = description;
  ensureNamedMeta('twitter:title').content = titleForSocial;
  ensureNamedMeta('twitter:description').content = description;

  if (meta.indexable && meta.canonicalPath) {
    const path = meta.canonicalPath.startsWith('/') ? meta.canonicalPath : `/${meta.canonicalPath}`;
    setCanonical(`${PUBLIC_ORIGIN}${path}`);
    ensurePropertyMeta('og:url').content = `${PUBLIC_ORIGIN}${path}`;
  } else {
    setCanonical(null);
  }
}
